import { noop } from "lodash";
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Navigate, Route, Routes, useParams } from "react-router";
import { Dimmer, Loader } from "semantic-ui-react";

import { ModalTypes, RestrictedResource } from "components";
import PublishConfirmationModal, {
  type EntityState,
} from "components/modal/PublishConfirmationModal/PublishConfirmationModal";
import RevertConfirmationModalTs from "components/modal/RevertConfirmationModalTs";
import { SwitchEntityTypes } from "enums/switchEntityTypes";
import {
  LockScormSuccess,
  ScormDiscardSuccess,
  ScormPublishSuccess,
} from "features/Application/services/realTimeNotification/events/library/libraryEvents";
import AssociatedPacks from "features/Licensing/Packs/AssociatedPacks/AssociatedPacks";
import { NotifyStepConfig } from "features/SystemNotifications/types";
import { useObservable } from "hooks/useObservable";
import ModalManager from "utils/ModalManager";
import Segments from "../../../../components/navigation/segments/Segments";
import {
  PublishedStatusTypes,
  RolePermissions,
  RouteNames,
  TemplateTypes,
  UsersGroupsContext,
} from "../../../../enums";
import { useRtn } from "../../../../hooks/useRtn";
import { useWizardStepsManager } from "../../../../hooks/useWizardStepsManager";
import { bindAction, type EnumDictionary, type ISegment } from "../../../../interfaces";
import { type AppDispatch, type RootState } from "../../../Application/globaltypes/redux";
import Restricted from "../../../Application/Restricted";
import Configure from "../Details/Configure";
import SettingsTab from "../Details/SettingsTab";
import { reset as resetSCORMAction } from "../state/slices/DetailsSlice";
import { resetTags } from "../state/slices/TagsSlice";
import { fetchItemAction } from "../state/thunks/DetailsThunk";
import { discardItemAction, publishItemAction } from "../state/thunks/EntityStateThunk";
import EditDetailsHeader from "./EditDetailsHeader";
import EditSubheaderContainer from "./EditSubheaderContainer";
import EditPeople from "./EditPeople";
import { resetState } from "../state/slices/EntityStateSlice";

enum EditSteps {
  Performance,
  Configuration,
  Settings,
  People,
  AssociatedPacks,
}

const pageIndexes = [
  EditSteps.Performance,
  EditSteps.Configuration,
  EditSteps.Settings,
  EditSteps.People,
  EditSteps.AssociatedPacks,
];

const entityStateRtns = [ScormPublishSuccess, ScormDiscardSuccess, LockScormSuccess];
const showLastModified = new Set([EditSteps.Configuration]);
const showReportLastUpdated = new Set([EditSteps.Performance]);
const overviewUrl = `/${RouteNames.contentScorm}`;

export type EditPropsAll = PropsFromRedux;

export const Edit = (props: EditPropsAll) => {
  const { fetchItem, discardItem, publishItem, resetSCORM, resetTags, resetEntityState, item, userId } = props;

  useEffect(() => {
    if (!props.isLoaded) {
      fetchItem(scormId);
    }

    return () => {
      resetSCORM();
      resetTags();
      resetEntityState();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isPublishedAndNotPurchased = useMemo(() => !item.isDraft && !item.isPurchased, [item]);
  const publishedStatus = useMemo(
    () => PublishedStatusTypes.ConvertToPublishedStatusType(!item.isDraft, item.hasBeenPublished),
    [item],
  );
  const [usersGroupsContext, setUsersGroupsContext] = useState(UsersGroupsContext.Groups);

  const params = useParams();
  const scormId = Number(params.id);

  const [wizardPages, { goToPage, goBack }, subscribeOnActiveIndexChanged] = useWizardStepsManager(pageIndexes);
  const publishObserver = useObservable<(onConfirm: () => void, entityState: EntityState) => void>();

  const [activeTab, setActiveTab] = useState<number>(0);
  const revertModalManager = useMemo(
    () => new ModalManager(ModalTypes.RevertSCORM, () => discardItem(scormId), false, userId),
    [discardItem, userId, scormId],
  );

  const pages: EnumDictionary<EditSteps, ISegment> = useMemo(() => {
    return {
      [EditSteps.Performance]: {
        to: "",
        label: "Performance",
        onClick: () => goToPage(EditSteps.Performance),
      },
      [EditSteps.Configuration]: {
        to: "configuration",
        label: "Configuration",
        onClick: () => goToPage(EditSteps.Configuration),
      },
      [EditSteps.Settings]: {
        to: "settings",
        label: "Settings",
        onClick: () => goToPage(EditSteps.Settings),
      },
      [EditSteps.People]: {
        to: "people",
        label: "People",
        onClick: () => goToPage(EditSteps.People),
      },
      [EditSteps.AssociatedPacks]: {
        to: "packs",
        label: "Associated Packs",
        onClick: () => goToPage(EditSteps.AssociatedPacks),
      },
    };
  }, [goToPage]);

  const [isValid, setIsValid] = useState(true);
  useLayoutEffect(() => {
    const handler = (_: EditSteps, activeStepIndex: EditSteps) => {
      pages[activeStepIndex].init?.();
      setActiveTab(activeStepIndex);
    };
    return subscribeOnActiveIndexChanged(handler);
  }, [pages, subscribeOnActiveIndexChanged]);

  const onEntityStateChange = useCallback(() => {
    fetchItem(scormId);
  }, [fetchItem, scormId]);

  useRtn(entityStateRtns, onEntityStateChange);

  const handlePublish = useCallback(() => {
    if (item.isDraft) {
      const entityState: EntityState = {
        itemsCount: item.settings.flows.length,
        entityId: item.id,
        templateType: item.hasBeenPublished ? TemplateTypes.SCORMChanged : TemplateTypes.AddedToSCORM,
        notifyConfigType: item.hasBeenPublished ? NotifyStepConfig.WithProgress : NotifyStepConfig.Default,
        entityType: SwitchEntityTypes.Scorm,
        hasBeenPublished: item.hasBeenPublished,
      };

      const cb = item.hasBeenPublished
        ? () => publishObserver.notify(noop, entityState)
        : () => publishItem(entityState.entityId);
      cb();
    }
  }, [publishItem, publishObserver, item]);

  const back = () => {
    if (!goBack()) {
      return overviewUrl;
    }
  };

  return (
    <Restricted
      permissions={[RolePermissions.AssetsCreate]}
      renderContent={(hasPermission: boolean) => (
        <div className="nested-content scrollable-content">
          <EditDetailsHeader
            scormId={scormId}
            overviewUrl={overviewUrl}
            title={item.title}
            canBeEdited={isPublishedAndNotPurchased}
            crudPermission={hasPermission}
            canBePublished={item.isDraft}
            isRevertVisible={item.hasBeenPublished}
            invalidFormDetails={!isValid}
            publishedStatus={publishedStatus}
            hideEntityStateButtons={activeTab === EditSteps.People}
            entityStateActions={{
              onRevert: () => revertModalManager.execute(),
              onPublish: handlePublish,
            }}
            back={back}
            canAddPacks={isPublishedAndNotPurchased}
            showAddPacksButton={activeTab === EditSteps.AssociatedPacks}
          />
          {item.hasBeenPublished && <RevertConfirmationModalTs modalManager={revertModalManager} />}
          <PublishConfirmationModal
            onTriggerPublishContentObserver={publishObserver}
            onConfirmAction={publishItem}
            contentType="SCORM Package"
            dependencyType="flow"
          />
          <Dimmer active={props.isLoading} inverted>
            <Loader />
          </Dimmer>
          <EditSubheaderContainer
            publishedStatus={publishedStatus}
            isUpdateInProgress={item.isDraft}
            lastModifiedDateTime={item.dateModified}
            showLastModified={showLastModified.has(activeTab)}
            showReportLastUpdated={showReportLastUpdated.has(activeTab)}
          >
            <Segments to={`${overviewUrl}/${scormId}`}>
              <Segments.Segment {...pages[EditSteps.Performance]} />
              <Segments.Segment {...pages[EditSteps.Configuration]} />
              <Segments.Segment {...pages[EditSteps.Settings]} />
              <Segments.Segment {...pages[EditSteps.People]} />
              <Segments.Segment {...pages[EditSteps.AssociatedPacks]} />
            </Segments>
          </EditSubheaderContainer>
          <div className="scrollable-content">
            <Routes>
              <Route
                path={pages[EditSteps.Performance].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <div>Under Construction</div>
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditSteps.Configuration].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <div className="edit-form">
                      <Configure
                        mode="edit"
                        entityId={scormId}
                        acceptHandlers={wizardPages[EditSteps.Configuration]}
                        disabled={!item.isDraft}
                        onIsValidChange={setIsValid}
                      />
                    </div>
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditSteps.Settings].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <div className="edit-form">
                      <SettingsTab acceptHandlers={wizardPages[EditSteps.Settings]} />
                    </div>
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditSteps.People].to}
                element={
                  <RestrictedResource permissions={[RolePermissions.AssetsView]}>
                    <EditPeople
                      scormId={scormId}
                      acceptHandlers={wizardPages[EditSteps.People]}
                      usersGroupsContext={usersGroupsContext}
                      onContextChanged={setUsersGroupsContext}
                    />
                  </RestrictedResource>
                }
              />
              <Route
                path={pages[EditSteps.AssociatedPacks].to}
                element={
                  <AssociatedPacks
                    acceptHandlers={wizardPages[EditSteps.AssociatedPacks]}
                    contentType="scorm"
                    contentId={scormId}
                    canAddPacks={isPublishedAndNotPurchased}
                    permissions={[RolePermissions.AssetsCreate]}
                  />
                }
              />
              <Route path="*" element={<Navigate to="../" replace />} />
            </Routes>
          </div>
        </div>
      )}
    />
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const scorm = state.library.scorm;
  return {
    item: scorm.details.item,
    isLoading: scorm.details.isLoading,
    isLoaded: scorm.details.isLoaded,
    userId: Number(state.userProfile.id),
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  discardItem: bindAction(discardItemAction, dispatch),
  fetchItem: bindAction(fetchItemAction, dispatch),
  publishItem: bindAction(publishItemAction, dispatch),
  resetSCORM: bindAction(resetSCORMAction, dispatch),
  resetTags: bindAction(resetTags, dispatch),
  resetEntityState: bindAction(resetState, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(Edit);
export default ConnectedComponent;
